import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Gavel,
  Users,
  Activity,
  IndianRupee,
  TrendingUp,
  Trophy,
  UserPlus,
  Percent,
  Download,
  RefreshCw,
  Search,
  AlertCircle,
  ArrowDown,
  ArrowUp,
} from "lucide-react";
import adminReportService, {
  AdminAuctionRow,
  AdminAuctionsReport,
  AdminDashboardReport,
  AdminUserRow,
  AdminUsersReport,
  ReportPeriod,
} from "../../../services/adminReportService";
import "./AdminReports.css";

type Tab = "overview" | "auctions" | "users";

const PERIODS: Array<{ value: ReportPeriod; label: string }> = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
  { value: "this_year", label: "This Year" },
  { value: "all", label: "All Time" },
];

const TYPE_LABEL: Record<string, string> = {
  english_no_tie: "English (No Tie)",
  english: "English",
  traffic_light: "Traffic Light",
};

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});
const formatInr = (n: number) => inr.format(n || 0);

const formatMoney = (n: number | null | undefined, currency = "INR") =>
  n == null
    ? "-"
    : new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency === "USD" ? "USD" : "INR",
        maximumFractionDigits: 0,
      }).format(n);

const formatDate = (v?: string | null) => {
  if (!v) return "-";
  const d = new Date(v.length === 10 ? `${v}T00:00:00` : v.replace(" ", "T"));
  return isNaN(d.getTime())
    ? "-"
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatTime = (t?: string | null) => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hh = parseInt(h, 10);
  if (isNaN(hh)) return "";
  return `${hh % 12 || 12}:${m} ${hh >= 12 ? "PM" : "AM"}`;
};

/* ------------------------------------------------------------------ */
/* CSV export (client side, from the rows currently on screen)         */
/* ------------------------------------------------------------------ */
const downloadCsv = (filename: string, header: string[], rows: Array<Array<string | number>>) => {
  const esc = (v: string | number) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/* ------------------------------------------------------------------ */
/* Small presentational pieces                                         */
/* ------------------------------------------------------------------ */
const Kpi: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: "blue" | "green" | "orange" | "violet";
}> = ({ icon, label, value, hint, tone = "blue" }) => (
  <div className={`arp-kpi arp-tone-${tone}`}>
    <div className="arp-kpi-icon">{icon}</div>
    <div className="arp-kpi-body">
      <span className="arp-kpi-label">{label}</span>
      <span className="arp-kpi-value">{value}</span>
      {hint && <span className="arp-kpi-hint">{hint}</span>}
    </div>
  </div>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`arp-badge arp-badge-${status}`}>{status}</span>
);

const Empty: React.FC<{ title: string; text?: string }> = ({ title, text }) => (
  <div className="arp-empty">
    <BarChart3 />
    <strong>{title}</strong>
    {text && <span>{text}</span>}
  </div>
);

const Loading: React.FC = () => (
  <div className="arp-loading">
    <div className="arp-spinner" />
    <span>Loading report...</span>
  </div>
);

/* Monthly trend: bars = auctions, line = bids. Plain SVG, no chart library. */
const TrendChart: React.FC<{ data: AdminDashboardReport["monthly_trends"] }> = ({ data }) => {
  const W = 640;
  const H = 230;
  const pad = { l: 34, r: 12, t: 14, b: 28 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const maxVal = Math.max(1, ...data.map((d) => Math.max(d.auctions, d.bids)));
  const niceMax = Math.ceil(maxVal / 4) * 4 || 4;
  const step = innerW / data.length;
  const barW = Math.min(26, step * 0.5);
  const y = (v: number) => pad.t + innerH - (v / niceMax) * innerH;
  const x = (i: number) => pad.l + step * i + step / 2;
  const line = data.map((d, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(d.bids)}`).join(" ");
  const ticks = [0, 1, 2, 3, 4].map((t) => (niceMax / 4) * t);

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="arp-chart" role="img" aria-label="Auctions and bids per month">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} className="arp-grid" />
            <text x={pad.l - 8} y={y(t) + 4} textAnchor="end" className="arp-axis">
              {t}
            </text>
          </g>
        ))}
        {data.map((d, i) => (
          <g key={d.month}>
            <title>{`${d.month}: ${d.auctions} auctions, ${d.bids} bids`}</title>
            <rect
              x={x(i) - barW / 2}
              y={y(d.auctions)}
              width={barW}
              height={Math.max(0, pad.t + innerH - y(d.auctions))}
              rx={4}
              className="arp-bar"
            />
            <text x={x(i)} y={H - 8} textAnchor="middle" className="arp-axis">
              {d.month}
            </text>
          </g>
        ))}
        <path d={line} className="arp-line" fill="none" />
        {data.map((d, i) => (
          <circle key={d.month} cx={x(i)} cy={y(d.bids)} r={3.5} className="arp-dot" />
        ))}
      </svg>
      <div className="arp-legend">
        <span>
          <i className="arp-swatch arp-swatch-bar" /> Auctions
        </span>
        <span>
          <i className="arp-swatch arp-swatch-line" /> Bids
        </span>
      </div>
    </div>
  );
};

/* Horizontal stacked bar used for status / mode splits */
const Split: React.FC<{ parts: Array<{ label: string; value: number; cls: string }> }> = ({ parts }) => {
  const total = parts.reduce((s, p) => s + p.value, 0);
  return (
    <div>
      <div className="arp-split">
        {total === 0 ? (
          <span className="arp-split-empty" />
        ) : (
          parts
            .filter((p) => p.value > 0)
            .map((p) => <span key={p.label} className={`arp-split-seg ${p.cls}`} style={{ width: `${(p.value / total) * 100}%` }} />)
        )}
      </div>
      <ul className="arp-split-list">
        {parts.map((p) => (
          <li key={p.label}>
            <i className={`arp-swatch ${p.cls}`} />
            <span>{p.label}</span>
            <strong>{p.value}</strong>
            <em>{total ? `${Math.round((p.value / total) * 100)}%` : "0%"}</em>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */
const AdminReports: React.FC = () => {
  const [tab, setTab] = useState<Tab>("overview");
  const [period, setPeriod] = useState<ReportPeriod>("this_month");

  const [dashboard, setDashboard] = useState<AdminDashboardReport | null>(null);
  const [auctions, setAuctions] = useState<AdminAuctionsReport | null>(null);
  const [users, setUsers] = useState<AdminUsersReport | null>(null);

  const [auctionStatus, setAuctionStatus] = useState("");
  const [userRole, setUserRole] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // debounce the search box so we don't query on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // reset the search when switching tabs
  useEffect(() => {
    setSearchInput("");
    setSearch("");
  }, [tab]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === "overview") setDashboard(await adminReportService.getDashboard(period));
      else if (tab === "auctions")
        setAuctions(await adminReportService.getAuctions(period, auctionStatus || undefined, search || undefined));
      else setUsers(await adminReportService.getUsers(period, userRole || undefined, search || undefined));
    } catch (e: any) {
      setError(e?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [tab, period, auctionStatus, userRole, search]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const periodLabel = PERIODS.find((p) => p.value === period)?.label ?? "";

  const exportAuctions = () => {
    if (!auctions) return;
    downloadCsv(
      `auctions-report-${period}.csv`,
      ["Auction No", "Title", "Status", "Date", "Mode", "Type", "Created By", "Company", "Participants", "Bids", "Best Bid", "Currency", "Winner", "Winner Company"],
      auctions.auctions.map((a) => [
        a.auction_no,
        a.title,
        a.status,
        a.auction_date,
        a.mode,
        TYPE_LABEL[a.type] || a.type,
        a.creator_name || "",
        a.creator_company || "",
        a.participants,
        a.total_bids,
        a.best_amount ?? "",
        a.currency,
        a.winner_name || "",
        a.winner_company || "",
      ])
    );
  };

  const exportUsers = () => {
    if (!users) return;
    downloadCsv(
      `users-report-${period}.csv`,
      ["Name", "Company", "Phone", "Email", "Role", "Status", "Registered", "Auctions Created", "Bids Placed", "Auctions Bid On", "Wins", "Won Value"],
      users.users.map((u) => [
        u.name || "",
        u.company || "",
        u.phone || "",
        u.email || "",
        u.role,
        u.status,
        u.registered_at,
        u.auctions_created,
        u.bids_placed,
        u.auctions_bid_on,
        u.wins,
        u.won_value,
      ])
    );
  };

  const k = dashboard?.kpis;
  const trendHasData = useMemo(
    () => !!dashboard && dashboard.monthly_trends.some((m) => m.auctions > 0 || m.bids > 0),
    [dashboard]
  );

  return (
    <div className="arp-root">
      {/* ---------- Header ---------- */}
      <header className="arp-header">
        <div>
          <h1 className="arp-title">
            <span className="arp-title-icon">
              <BarChart3 />
            </span>
            Reports &amp; Analytics
          </h1>
          <p className="arp-subtitle">Platform-wide auction, bidding and user performance</p>
        </div>
        <div className="arp-controls">
          <div className="arp-segment" role="group" aria-label="Report period">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                type="button"
                className={period === p.value ? "active" : ""}
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button type="button" className="arp-btn" onClick={() => setRefreshKey((n) => n + 1)} disabled={loading}>
            <RefreshCw className={loading ? "arp-spin" : ""} />
            Refresh
          </button>
        </div>
      </header>

      {/* ---------- Tabs ---------- */}
      <nav className="arp-tabs" role="tablist">
        {(
          [
            ["overview", "Overview", <BarChart3 key="o" />],
            ["auctions", "Auctions", <Gavel key="a" />],
            ["users", "Users", <Users key="u" />],
          ] as Array<[Tab, string, React.ReactNode]>
        ).map(([id, label, icon]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`arp-tab ${tab === id ? "active" : ""}`}
            onClick={() => setTab(id)}
          >
            {icon}
            {label}
          </button>
        ))}
      </nav>

      {error && (
        <div className="arp-error" role="alert">
          <AlertCircle />
          <span>{error}</span>
          <button type="button" onClick={() => setRefreshKey((n) => n + 1)}>
            Retry
          </button>
        </div>
      )}

      {/* ================= OVERVIEW ================= */}
      {tab === "overview" && (
        <>
          {loading && !dashboard ? (
            <Loading />
          ) : dashboard && k ? (
            <div className={loading ? "arp-fade" : ""}>
              <section className="arp-kpis">
                <Kpi
                  tone="blue"
                  icon={<Gavel />}
                  label="Total Auctions"
                  value={k.total_auctions}
                  hint={`${periodLabel} - ${dashboard.mode_breakdown.reverse} reverse, ${dashboard.mode_breakdown.forward} forward`}
                />
                <Kpi
                  tone="green"
                  icon={<Activity />}
                  label="Live Now"
                  value={k.live_auctions}
                  hint={`${k.upcoming_auctions} upcoming`}
                />
                <Kpi
                  tone="violet"
                  icon={<Trophy />}
                  label="Completed"
                  value={k.completed_auctions}
                  hint={`${k.completed_without_bids} closed with no bids`}
                />
                <Kpi
                  tone="orange"
                  icon={<IndianRupee />}
                  label="Deal Value"
                  value={formatInr(k.deal_value)}
                  hint="Sum of winning bids on completed auctions"
                />
                <Kpi
                  tone="blue"
                  icon={<TrendingUp />}
                  label="Total Bids"
                  value={k.total_bids.toLocaleString("en-IN")}
                  hint={`${k.avg_bids_per_auction} bids per auction`}
                />
                <Kpi
                  tone="green"
                  icon={<Users />}
                  label="Active Bidders"
                  value={k.active_bidders}
                  hint="Distinct users who placed a bid"
                />
                <Kpi
                  tone="violet"
                  icon={<UserPlus />}
                  label="Registered Users"
                  value={k.total_users.toLocaleString("en-IN")}
                  hint={`${k.new_users} new - ${k.channel_partners} channel partners`}
                />
                <Kpi
                  tone="orange"
                  icon={<Percent />}
                  label="Participation Rate"
                  value={`${k.participation_rate}%`}
                  hint="Invited participants who joined"
                />
              </section>

              <section className="arp-grid-2">
                <div className="arp-card arp-span-2">
                  <div className="arp-card-head">
                    <h2>Last 12 Months</h2>
                    <span>Auctions and bids by month</span>
                  </div>
                  {trendHasData ? <TrendChart data={dashboard.monthly_trends} /> : <Empty title="No activity yet" text="Trends appear once auctions are held." />}
                </div>

                <div className="arp-card">
                  <div className="arp-card-head">
                    <h2>Auction Status</h2>
                    <span>{periodLabel}</span>
                  </div>
                  <Split
                    parts={[
                      { label: "Live", value: k.live_auctions, cls: "arp-c-green" },
                      { label: "Upcoming", value: k.upcoming_auctions, cls: "arp-c-blue" },
                      { label: "Completed", value: k.completed_auctions, cls: "arp-c-violet" },
                    ]}
                  />
                </div>

                <div className="arp-card">
                  <div className="arp-card-head">
                    <h2>Auction Format</h2>
                    <span>{periodLabel}</span>
                  </div>
                  <Split
                    parts={[
                      { label: "Reverse (lowest wins)", value: dashboard.mode_breakdown.reverse, cls: "arp-c-blue" },
                      { label: "Forward (highest wins)", value: dashboard.mode_breakdown.forward, cls: "arp-c-orange" },
                    ]}
                  />
                </div>
              </section>

              <section className="arp-grid-2">
                <div className="arp-card">
                  <div className="arp-card-head">
                    <h2>Most Active Auctions</h2>
                    <span>By number of bids</span>
                  </div>
                  {dashboard.top_auctions.length === 0 ? (
                    <Empty title="No bids in this period" />
                  ) : (
                    <table className="arp-table arp-table-compact">
                      <thead>
                        <tr>
                          <th>Auction</th>
                          <th className="r">Bids</th>
                          <th className="r">Bidders</th>
                          <th className="r">Best Bid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.top_auctions.map((a) => (
                          <tr key={a.id}>
                            <td>
                              <div className="arp-cell-title">{a.title}</div>
                              <StatusBadge status={a.status} />
                            </td>
                            <td className="r">{a.total_bids}</td>
                            <td className="r">{a.bidders}</td>
                            <td className="r">{formatMoney(a.best_amount, a.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="arp-card">
                  <div className="arp-card-head">
                    <h2>Top Bidders</h2>
                    <span>By number of bids</span>
                  </div>
                  {dashboard.top_bidders.length === 0 ? (
                    <Empty title="No bidders in this period" />
                  ) : (
                    <table className="arp-table arp-table-compact">
                      <thead>
                        <tr>
                          <th>Bidder</th>
                          <th className="r">Bids</th>
                          <th className="r">Auctions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.top_bidders.map((b, i) => (
                          <tr key={b.id}>
                            <td>
                              <div className="arp-person">
                                <span className="arp-rank">{i + 1}</span>
                                <div>
                                  <div className="arp-cell-title">{b.person_name || "-"}</div>
                                  <div className="arp-cell-sub">{b.company_name || "-"}</div>
                                </div>
                              </div>
                            </td>
                            <td className="r">{b.bids}</td>
                            <td className="r">{b.auctions}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>
            </div>
          ) : null}
        </>
      )}

      {/* ================= AUCTIONS ================= */}
      {tab === "auctions" && (
        <div className="arp-card arp-flush">
          <div className="arp-toolbar">
            <div className="arp-search">
              <Search />
              <input
                type="text"
                placeholder="Search by title, auction no or auctioneer..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <select className="arp-select" value={auctionStatus} onChange={(e) => setAuctionStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="live">Live</option>
              <option value="upcoming">Upcoming</option>
              <option value="completed">Completed</option>
            </select>
            <button type="button" className="arp-btn arp-btn-primary" onClick={exportAuctions} disabled={!auctions?.auctions.length}>
              <Download />
              Export CSV
            </button>
          </div>

          {auctions && (
            <div className="arp-chips">
              <span>
                <strong>{auctions.summary.total}</strong> auctions
              </span>
              <span>
                <strong>{auctions.summary.live}</strong> live
              </span>
              <span>
                <strong>{auctions.summary.upcoming}</strong> upcoming
              </span>
              <span>
                <strong>{auctions.summary.completed}</strong> completed
              </span>
              <span>
                <strong>{auctions.summary.total_bids}</strong> bids
              </span>
              <span>
                Deal value <strong>{formatInr(auctions.summary.deal_value)}</strong>
              </span>
            </div>
          )}

          {loading && !auctions ? (
            <Loading />
          ) : auctions && auctions.auctions.length === 0 ? (
            <Empty title="No auctions found" text="Try a different period, status or search." />
          ) : auctions ? (
            <div className={`arp-table-wrap ${loading ? "arp-fade" : ""}`}>
              <table className="arp-table">
                <thead>
                  <tr>
                    <th>Auction</th>
                    <th>Auctioneer</th>
                    <th>Format</th>
                    <th className="r">Participants</th>
                    <th className="r">Bids</th>
                    <th className="r">Best Bid</th>
                    <th>Winner</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {auctions.auctions.map((a: AdminAuctionRow) => (
                    <tr key={a.id}>
                      <td>
                        <div className="arp-cell-title">{a.title}</div>
                        <div className="arp-cell-sub">
                          {a.auction_no} - {formatDate(a.auction_date)} {formatTime(a.start_time)}
                        </div>
                      </td>
                      <td>
                        <div className="arp-cell-title">{a.creator_name || "-"}</div>
                        <div className="arp-cell-sub">{a.creator_company || "-"}</div>
                      </td>
                      <td>
                        <span className={`arp-mode arp-mode-${a.mode}`}>
                          {a.mode === "forward" ? <ArrowUp /> : <ArrowDown />}
                          {a.mode === "forward" ? "Forward" : "Reverse"}
                        </span>
                        <div className="arp-cell-sub">{TYPE_LABEL[a.type] || a.type}</div>
                      </td>
                      <td className="r">
                        {a.joined}
                        <span className="arp-muted"> / {a.participants}</span>
                      </td>
                      <td className="r">{a.total_bids}</td>
                      <td className="r arp-strong">{formatMoney(a.best_amount, a.currency)}</td>
                      <td>
                        {a.status === "completed" && a.winner_company ? (
                          <>
                            <div className="arp-cell-title">{a.winner_company}</div>
                            <div className="arp-cell-sub">{a.winner_name}</div>
                          </>
                        ) : (
                          <span className="arp-muted">{a.status === "completed" ? "No bids" : "-"}</span>
                        )}
                      </td>
                      <td>
                        <StatusBadge status={a.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      )}

      {/* ================= USERS ================= */}
      {tab === "users" && (
        <div className="arp-card arp-flush">
          <div className="arp-toolbar">
            <div className="arp-search">
              <Search />
              <input
                type="text"
                placeholder="Search by name, company, phone or email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <select className="arp-select" value={userRole} onChange={(e) => setUserRole(e.target.value)}>
              <option value="">All roles</option>
              <option value="user">Users</option>
              <option value="channel_partner">Channel partners</option>
            </select>
            <button type="button" className="arp-btn arp-btn-primary" onClick={exportUsers} disabled={!users?.users.length}>
              <Download />
              Export CSV
            </button>
          </div>

          {users && (
            <div className="arp-chips">
              <span>
                <strong>{users.summary.total}</strong> users
              </span>
              <span>
                <strong>{users.summary.active}</strong> active in period
              </span>
              <span>
                <strong>{users.summary.channel_partners}</strong> channel partners
              </span>
              <span>
                <strong>{users.summary.blocked}</strong> blocked
              </span>
              <span>
                <strong>{users.summary.total_bids}</strong> bids
              </span>
              <span>
                <strong>{users.summary.total_wins}</strong> wins
              </span>
            </div>
          )}

          {loading && !users ? (
            <Loading />
          ) : users && users.users.length === 0 ? (
            <Empty title="No users found" text="Try a different search or role." />
          ) : users ? (
            <div className={`arp-table-wrap ${loading ? "arp-fade" : ""}`}>
              <table className="arp-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Contact</th>
                    <th>Role</th>
                    <th className="r">Auctions Created</th>
                    <th className="r">Bids</th>
                    <th className="r">Wins</th>
                    <th className="r">Won Value</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {users.users.map((u: AdminUserRow) => (
                    <tr key={u.id}>
                      <td>
                        <div className="arp-cell-title">{u.name || "-"}</div>
                        <div className="arp-cell-sub">{u.company || "-"}</div>
                      </td>
                      <td>
                        <div className="arp-cell-title">{u.phone || "-"}</div>
                        <div className="arp-cell-sub">{u.email || "-"}</div>
                      </td>
                      <td>
                        <span className={`arp-role ${u.role === "channel_partner" ? "arp-role-cp" : ""}`}>
                          {u.role === "channel_partner" ? "Channel Partner" : "User"}
                        </span>
                        <div className="arp-cell-sub">Joined {formatDate(u.registered_at)}</div>
                      </td>
                      <td className="r">{u.auctions_created}</td>
                      <td className="r">
                        {u.bids_placed}
                        {u.auctions_bid_on > 0 && <span className="arp-muted"> in {u.auctions_bid_on}</span>}
                      </td>
                      <td className="r">{u.wins}</td>
                      <td className="r arp-strong">{u.won_value ? formatInr(u.won_value) : "-"}</td>
                      <td>
                        <StatusBadge status={u.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default AdminReports;
